import { classifyShift } from '../award-engines/shift-classifier';
import { AGED_CARE_SHIFT_RULES } from '../award-data/aged-care';
import type { ShiftPayWithOvertimeInput } from './daily-overtime-input';
import { allocateGeneralStaffOvertime, generalStaffWorkedSegments } from './general-staff-overtime';

export type GeneralStaffPriorHoursRules = Pick<
  ShiftPayWithOvertimeInput,
  | 'employmentType'
  | 'employeeCategory'
  | 'ordinaryHoursLimitPerShift'
  | 'ordinaryHoursLimitPerWeek'
  | 'generalStaffWeeklyWorkLimit'
  | 'generalStaffDailyLimitBasis'
>;

/** A disclosed upper bound when historical ordinary settlement is absent.
 * Uses the existing EA hours allocator, never historical prices or monetary rates.
 * Missing break placement maximises plausible ordinary carry (protecting current
 * overtime); historical rolling-cap settlements are unknown, not assumed paid.
 */
export function assumeGeneralStaffPriorOrdinaryHours(
  input: GeneralStaffPriorHoursRules & {
    actualStart: string;
    actualEnd: string;
    workedHours: number;
    timezone: string;
    workedEarlierToday: number;
    workedEarlierThisWeek: number;
    recordedBreakIntervals?: readonly { startTime: string; endTime: string }[];
  },
) {
  const startTime = new Date(input.actualStart),
    endTime = new Date(input.actualEnd);
  const span = (endTime.getTime() - startTime.getTime()) / 3_600_000;
  if (
    !Number.isFinite(span) ||
    span <= 0 ||
    !Number.isFinite(input.workedHours) ||
    input.workedHours < 0 ||
    input.workedHours > span + 1e-8
  )
    throw new Error('pricing_gs_prior_worked_hours_missing');
  const engineInput: ShiftPayWithOvertimeInput = {
    // This helper never selects a rate or calls a pay engine.
    schadsLevel: '',
    employmentType: input.employmentType,
    employeeCategory: input.employeeCategory,
    ordinaryHoursLimitPerShift: input.ordinaryHoursLimitPerShift,
    ordinaryHoursLimitPerWeek: input.ordinaryHoursLimitPerWeek,
    generalStaffWeeklyWorkLimit: input.generalStaffWeeklyWorkLimit,
    generalStaffDailyLimitBasis: input.generalStaffDailyLimitBasis,
    startTime,
    endTime,
    timezone: input.timezone,
    breakMinutes: Math.max(0, span - input.workedHours) * 60,
    generalStaffHoursWorkedEarlierToday: input.workedEarlierToday,
    generalStaffHoursWorkedEarlierThisWeek: input.workedEarlierThisWeek,
  };
  const segments = classifyShift({
    startTime,
    endTime,
    breakMinutes: 0,
    timezone: input.timezone,
    isPublicHoliday: false,
    shiftCharacterRules: AGED_CARE_SHIFT_RULES,
    dominantDayRule: 'STRICT_MAJORITY',
  }).segments;
  const sumOrdinary = (cells: typeof segments) =>
    allocateGeneralStaffOvertime(engineInput, cells)
      .filter((cell) => cell.allocation === 'ORDINARY')
      .reduce((n, cell) => n + cell.hours, 0);
  if (input.recordedBreakIntervals !== undefined) {
    const breaks = input.recordedBreakIntervals
      .map((b) => ({
        start: Date.parse(b.startTime),
        end: Date.parse(b.endTime),
      }))
      .sort((a, b) => a.start - b.start);
    if (
      breaks.some(
        (b, i) =>
          !Number.isFinite(b.start) ||
          !Number.isFinite(b.end) ||
          b.end <= b.start ||
          b.start < startTime.getTime() ||
          b.end > endTime.getTime() ||
          (i > 0 && b.start < breaks[i - 1]!.end),
      ) ||
      Math.abs(
        breaks.reduce((n, b) => n + (b.end - b.start) / 3_600_000, 0) - (span - input.workedHours),
      ) > 0.0001
    )
      throw new Error('pricing_gs_break_geometry_invalid');
    let cells = segments;
    for (const b of breaks)
      cells = cells.flatMap((cell) =>
        [
          [cell.startTime.getTime(), Math.min(cell.endTime.getTime(), b.start)],
          [Math.max(cell.startTime.getTime(), b.end), cell.endTime.getTime()],
        ].flatMap(([a, z]) =>
          a! < z!
            ? [
                {
                  ...cell,
                  startTime: new Date(a!),
                  endTime: new Date(z!),
                  hours: (z! - a!) / 3_600_000,
                },
              ]
            : [],
        ),
      );
    return {
      ordinaryHours: Math.min(input.workedHours, sumOrdinary(cells)),
      breakPlacementAssumed: false,
      recordedBreakIntervals: input.recordedBreakIntervals,
      basis: 'APPROVED_WORK_GEOMETRY_MAX_ORDINARY_NO_HISTORICAL_SETTLEMENT' as const,
    };
  }
  const worked = generalStaffWorkedSegments(engineInput, segments, (breakStart, breakEnd) => {
    const placed = segments.flatMap((cell) =>
      [
        [cell.startTime.getTime(), Math.min(cell.endTime.getTime(), breakStart.getTime())],
        [Math.max(cell.startTime.getTime(), breakEnd.getTime()), cell.endTime.getTime()],
      ].flatMap(([a, b]) =>
        a! < b!
          ? [
              {
                ...cell,
                startTime: new Date(a!),
                endTime: new Date(b!),
                hours: (b! - a!) / 3_600_000,
              },
            ]
          : [],
      ),
    );
    return sumOrdinary(placed);
  });
  return {
    ordinaryHours: sumOrdinary(worked.segments),
    breakPlacementAssumed: engineInput.breakMinutes! > 0,
    basis: 'APPROVED_WORK_GEOMETRY_MAX_ORDINARY_NO_HISTORICAL_SETTLEMENT' as const,
  };
}
